import { ScrollView } from "react-native";
import { useLayoutEffect } from "react";
import { useLocalSearchParams, useNavigation } from "expo-router";
import { View, Text, ScreenContainer, Card } from "@src/components";
import { DataStateView } from "@src/components/screen-state";
import { WeeklySchedule } from "@src/features/schedules/components/WeeklySchedule";
import { useAlarm } from "@src/services/api/hooks";
import type { AlarmSchedule } from "@visioni/a3s-alarm-schedules";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";

export default function AppliedScheduleScreen() {
  const navigation = useNavigation();
  const { tenantId, siteId, cameraKey } = useLocalSearchParams<{
    tenantId: string;
    siteId: string;
    cameraKey: string;
  }>();
  const fallbackRoute =
    tenantId && siteId && cameraKey ? `/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarm` : "/alarms";
  const { headerLeft } = useHeaderBackButton(fallbackRoute);
  const { data: alarm, isLoading, isFetching, error, refetch } = useAlarm(siteId, cameraKey);

  useLayoutEffect((): void => {
    navigation.setOptions({
      title: "Currently Applied Schedule",
      headerBackButtonDisplayMode: "minimal",
      headerLeft,
    });
  }, [navigation, headerLeft]);

  // Check if alarm has applied schedule (for isEmpty check)
  const hasAppliedSchedule = (alarmData: NonNullable<typeof alarm>) =>
    !alarmData.applied || typeof alarmData.applied !== "object";

  // Render applied schedule content
  const renderAppliedScheduleContent = (alarmData: NonNullable<typeof alarm>) => {
    // This check is for type safety - DataStateView's isEmpty handles the empty state
    if (!alarmData.applied || typeof alarmData.applied !== "object") {
      return null;
    }

    return (
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 40 }}>
        <View px="5xl" py="4xl" bg="background">
          <Card>
            <View mb="3xl">
              <Text textStyle="sm/medium" mb="xs">
                Currently Applied Schedule
              </Text>
              {alarmData.applied.at ?
                <Text textStyle="xs/regular" color="textMuted" mb="3xl">
                  Applied{" "}
                  {new Intl.DateTimeFormat(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  }).format(new Date(alarmData.applied.at))}
                </Text>
              : <View mb="3xl" />}
              <WeeklySchedule
                schedule={alarmData.applied.schedule as AlarmSchedule}
                exceptions={alarmData.settings?.exceptions ?? []}
                timezone={
                  alarmData.settings?.timezone ??
                  alarmData.applied.schedule?.metadata?.timezone ??
                  Intl.DateTimeFormat().resolvedOptions().timeZone
                }
              />
            </View>
          </Card>
        </View>
      </ScrollView>
    );
  };

  return (
    <ScreenContainer edges={[]}>
      <DataStateView
        data={alarm}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        refetch={refetch}
        isEmpty={hasAppliedSchedule}
        loadingMessage="Loading applied schedule..."
        errorTitle="Error Loading Schedule"
        errorMessage="Failed to load applied schedule."
        emptyState={{
          icon: "calendarOutline",
          title: "No Applied Schedule",
          message: "No schedule is currently applied to this camera.",
        }}
      >
        {renderAppliedScheduleContent}
      </DataStateView>
    </ScreenContainer>
  );
}
